import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Star, Vote, Music, Users, Sparkles, ChevronRight, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_SUPPORTERS = [
  { name: 'Malo Le Guen', stars: 3 },
  { name: 'Anna Kerbrat', stars: 2 },
  { name: 'Yann Le Dantec', stars: 5 },
  { name: 'Claire Even', stars: 1 },
  { name: 'Tudal M.', stars: 4 },
  { name: 'Rozenn Le Berre', stars: 2 },
  { name: 'Erwan K.', stars: 3 },
  { name: 'Marina Le Floch', stars: 1 },
  { name: 'Gwenn & Lena', stars: 6 },
  { name: 'Famille Riou', stars: 2 },
  { name: 'Nolwenn B.', stars: 4 },
  { name: 'Mael Jouan', stars: 1 },
];

const CONSTELLATIONS = [
  {
    id: 'orion',
    name: 'Orion',
    stars: [
      { key: 'betelgeuse', x: 24, y: 20, mag: 1.5 },
      { key: 'bellatrix', x: 33, y: 22, mag: 1.8 },
      { key: 'alnitak', x: 27, y: 30, mag: 2.2 },
      { key: 'alnilam', x: 30, y: 31, mag: 1.7 },
      { key: 'mintaka', x: 33, y: 32, mag: 2.3 },
      { key: 'saiph', x: 27, y: 42, mag: 2.1 },
      { key: 'rigel', x: 36, y: 43, mag: 1.4 },
    ],
    links: [
      ['betelgeuse', 'bellatrix'],
      ['betelgeuse', 'alnitak'],
      ['bellatrix', 'mintaka'],
      ['alnitak', 'alnilam'],
      ['alnilam', 'mintaka'],
      ['alnitak', 'saiph'],
      ['mintaka', 'rigel'],
      ['saiph', 'rigel'],
    ],
    label: { x: 22, y: 14 },
  },
  {
    id: 'cassiopeia',
    name: 'Cassiopee',
    stars: [
      { key: 'schedar', x: 58, y: 14, mag: 2.2 },
      { key: 'caph', x: 63, y: 18, mag: 2.4 },
      { key: 'gamma', x: 68, y: 14, mag: 2.5 },
      { key: 'ruchbah', x: 74, y: 19, mag: 2.7 },
      { key: 'segin', x: 80, y: 13, mag: 3.0 },
    ],
    links: [
      ['schedar', 'caph'],
      ['caph', 'gamma'],
      ['gamma', 'ruchbah'],
      ['ruchbah', 'segin'],
    ],
    label: { x: 66, y: 8 },
  },
  {
    id: 'ursa_major',
    name: 'Grande Ourse',
    stars: [
      { key: 'dubhe', x: 13, y: 50, mag: 1.8 },
      { key: 'merak', x: 20, y: 55, mag: 2.3 },
      { key: 'phecda', x: 27, y: 53, mag: 2.4 },
      { key: 'megrez', x: 31, y: 47, mag: 3.2 },
      { key: 'alioth', x: 38, y: 44, mag: 1.9 },
      { key: 'mizar', x: 45, y: 47, mag: 2.2 },
      { key: 'alkaid', x: 52, y: 52, mag: 1.9 },
    ],
    links: [
      ['dubhe', 'merak'],
      ['merak', 'phecda'],
      ['phecda', 'megrez'],
      ['megrez', 'dubhe'],
      ['megrez', 'alioth'],
      ['alioth', 'mizar'],
      ['mizar', 'alkaid'],
    ],
    label: { x: 10, y: 61 },
  },
  {
    id: 'cygnus',
    name: 'Cygne',
    stars: [
      { key: 'deneb', x: 66, y: 37, mag: 1.3 },
      { key: 'sadr', x: 70, y: 45, mag: 2.3 },
      { key: 'gienah', x: 63, y: 51, mag: 2.5 },
      { key: 'delta', x: 76, y: 52, mag: 2.9 },
      { key: 'albireo', x: 70, y: 60, mag: 3.1 },
    ],
    links: [
      ['deneb', 'sadr'],
      ['sadr', 'albireo'],
      ['sadr', 'gienah'],
      ['sadr', 'delta'],
    ],
    label: { x: 73, y: 34 },
  },
];

const buildSkyMap = (supporters) => {
  const supporterList = supporters.length > 0 ? supporters : FALLBACK_SUPPORTERS;
  const allStars = [];
  const allLinks = [];
  let supporterCursor = 0;

  CONSTELLATIONS.forEach((constellation) => {
    const starMap = {};

    constellation.stars.forEach((star, index) => {
      const globalId = `${constellation.id}-${star.key}`;
      const isSupporterStar = index % 2 === 0;
      const supporter = isSupporterStar ? supporterList[supporterCursor++ % supporterList.length] : null;
      const normalizedMag = Math.max(1.1, 3.3 - star.mag);

      const node = {
        id: globalId,
        x: star.x,
        y: star.y,
        constellation: constellation.name,
        labelX: star.x + 1.4,
        labelY: star.y - 1.8,
        mag: star.mag,
        size: normalizedMag,
        supporter,
      };

      starMap[star.key] = node;
      allStars.push(node);
    });

    constellation.links.forEach(([from, to]) => {
      if (starMap[from] && starMap[to]) {
        allLinks.push([starMap[from], starMap[to]]);
      }
    });
  });

  return { allStars, allLinks };
};

const buildBackgroundStars = (count = 220) => {
  let seed = 1337;
  const rand = () => {
    seed = (seed * 48271) % 2147483647;
    return seed / 2147483647;
  };

  return Array.from({ length: count }).map((_, index) => ({
    id: `bg-star-${index}`,
    x: 1 + rand() * 98,
    y: 1 + rand() * 68,
    r: 0.07 + rand() * 0.18,
    opacity: 0.2 + rand() * 0.6,
    delay: rand() * 4,
    duration: 1.8 + rand() * 3.2,
  }));
};

const isLikelyTestSupporter = (person) => {
  const raw = `${person?.name || ''} ${person?.display_name || ''} ${person?.email || ''}`.toLowerCase();
  return /(test|pytest|webhook|log\s*check|@example\.com)/i.test(raw);
};

const ConstellationSky = ({ supporters }) => {
  const { allStars, allLinks } = useMemo(() => buildSkyMap(supporters), [supporters]);
  const backgroundStars = useMemo(() => buildBackgroundStars(), []);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [pointerOffset, setPointerOffset] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const handleMouseDown = (event) => {
    dragStateRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      originX: dragOffset.x,
      originY: dragOffset.y,
    };
  };

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const normalizedX = (event.clientX - rect.left) / rect.width;
    const normalizedY = (event.clientY - rect.top) / rect.height;

    setPointerOffset({
      x: (normalizedX - 0.5) * 14,
      y: (normalizedY - 0.5) * 10,
    });

    if (!dragStateRef.current.active) return;

    const deltaX = event.clientX - dragStateRef.current.startX;
    const deltaY = event.clientY - dragStateRef.current.startY;
    setDragOffset({
      x: clamp(dragStateRef.current.originX + deltaX, -60, 60),
      y: clamp(dragStateRef.current.originY + deltaY, -45, 45),
    });
  };

  const stopDragging = () => {
    dragStateRef.current.active = false;
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    dragStateRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      originX: dragOffset.x,
      originY: dragOffset.y,
    };
  };

  const handleTouchMove = (event) => {
    const touch = event.touches[0];
    if (!touch || !dragStateRef.current.active) return;

    const deltaX = touch.clientX - dragStateRef.current.startX;
    const deltaY = touch.clientY - dragStateRef.current.startY;
    setDragOffset({
      x: clamp(dragStateRef.current.originX + deltaX, -60, 60),
      y: clamp(dragStateRef.current.originY + deltaY, -45, 45),
    });
  };

  const skyTransform = {
    transform: `translate3d(${dragOffset.x + pointerOffset.x}px, ${dragOffset.y + pointerOffset.y}px, 0) scale(1.06)`,
  };

  return (
    <div
      className="absolute inset-0 overflow-hidden cursor-grab active:cursor-grabbing"
      aria-hidden="true"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDragging}
      onMouseLeave={stopDragging}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={stopDragging}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(77,183,255,0.2),transparent_42%),radial-gradient(circle_at_78%_15%,rgba(159,124,255,0.18),transparent_36%),radial-gradient(circle_at_50%_78%,rgba(255,215,120,0.16),transparent_50%),radial-gradient(circle_at_50%_50%,rgba(7,10,20,0.95),rgba(2,3,8,1))]" />
      <div className="absolute inset-0 transition-transform duration-150" style={skyTransform}>
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 70" preserveAspectRatio="none">
          {backgroundStars.map((star) => (
            <circle
              key={star.id}
              cx={star.x}
              cy={star.y}
              r={star.r}
              fill="rgba(255,255,255,0.95)"
              opacity={star.opacity}
              style={{ animation: `pulse ${star.duration}s ease-in-out ${star.delay}s infinite` }}
            />
          ))}

          {allLinks.map(([from, to], idx) => (
            <line
              key={`constellation-line-${idx}`}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke="rgba(136, 210, 255, 0.42)"
              strokeWidth="0.14"
              strokeDasharray="0.45 0.35"
            />
          ))}

          {CONSTELLATIONS.map((constellation) => (
            <text
              key={`label-${constellation.id}`}
              x={constellation.label.x}
              y={constellation.label.y}
              fill="rgba(172, 223, 255, 0.5)"
              fontSize="1.1"
              letterSpacing="0.15"
            >
              {constellation.name}
            </text>
          ))}

          {allStars.map((node, idx) => (
            <g key={node.id}>
              <circle
                cx={node.x}
                cy={node.y}
                r={node.size * 0.26 + (node.supporter ? 0.2 : 0)}
                fill={node.supporter ? 'rgba(255, 223, 133, 0.98)' : 'rgba(222, 242, 255, 0.95)'}
                style={{ animation: `pulse ${2.2 + (idx % 4) * 0.35}s ease-in-out ${idx * 0.1}s infinite` }}
              />
              <circle
                cx={node.x}
                cy={node.y}
                r={node.supporter ? '1.8' : '1.2'}
                fill="transparent"
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
              />
              {hoveredNode === node.id && node.supporter && (
                <g>
                  <rect
                    x={node.labelX - 0.7}
                    y={node.labelY - 2.6}
                    width={Math.max(10, node.supporter.name.length * 0.95 + 5.5)}
                    height="3.3"
                    rx="0.8"
                    fill="rgba(10, 12, 22, 0.88)"
                    stroke="rgba(255, 215, 0, 0.45)"
                    strokeWidth="0.15"
                  />
                  <text
                    x={node.labelX}
                    y={node.labelY - 0.9}
                    fill="rgba(255,255,255,0.95)"
                    fontSize="1.55"
                    textAnchor="start"
                    dominantBaseline="middle"
                  >
                    {node.supporter.name} • {node.supporter.stars || 1}
                  </text>
                </g>
              )}
            </g>
          ))}
        </svg>
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/35 via-obsidian/70 to-obsidian" />
    </div>
  );
};

// Animated Counter
const AnimatedCounter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <span>{count.toLocaleString()}</span>;
};

const Home = () => {
  const [stats, setStats] = useState({ total_proposals: 0, total_votes: 0, total_users: 0 });
  const [phase, setPhase] = useState({ current_phase: 1 });
  const [nominations, setNominations] = useState([]);
  const [supporters, setSupporters] = useState(FALLBACK_SUPPORTERS);
  const [resettingVotes, setResettingVotes] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const requestTs = Date.now();
    try {
      const [statsRes, phaseRes, nomRes, supportersRes] = await Promise.allSettled([
        axios.get(`${API}/stats`, { params: { t: requestTs } }),
        axios.get(`${API}/phase`),
        axios.get(`${API}/nominations?approved_only=true`),
        axios.get(`${API}/supporters/stars?limit=12`),
      ]);

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      }

      if (phaseRes.status === 'fulfilled') {
        setPhase(phaseRes.value.data);
      }

      if (nomRes.status === 'fulfilled') {
        setNominations(nomRes.value.data.slice(0, 4));
      }

      if (supportersRes.status === 'fulfilled' && Array.isArray(supportersRes.value.data) && supportersRes.value.data.length > 0) {
        const mappedSupporters = supportersRes.value.data
          .map((person) => ({
            name: person.name || person.display_name,
            stars: Number(person.stars || person.quantity || 1),
            email: person.email || '',
          }))
          .filter((person) => person.name && !isLikelyTestSupporter(person))
          .slice(0, 12);

        if (mappedSupporters.length > 0) {
          setSupporters(mappedSupporters);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const steps = [
    { num: '1', title: 'Proposer', desc: 'Soumettez vos artistes favoris', icon: Sparkles },
    { num: '2', title: 'Voter', desc: 'Élisez les meilleurs', icon: Vote },
    { num: '3', title: 'Gala', desc: 'Célébrez les lauréats', icon: Star },
  ];

  const categories = [
    { id: 'artist', name: 'Artiste de l\'année' },
    { id: 'album', name: 'Album de l\'année' },
    { id: 'festival', name: 'Festival de l\'année' },
    { id: 'bagad', name: 'Titre de l\'année' },
  ];

  const handleResetVotes = async () => {
    const firstCheck = window.confirm('Attention: cette action va remettre tous les votes a zero. Voulez-vous continuer ?');
    if (!firstCheck) return;

    const typedConfirmation = window.prompt('Confirmez en tapant RESET pour supprimer tous les votes:');
    if (typedConfirmation !== 'RESET') {
      window.alert('Operation annulee: confirmation invalide.');
      return;
    }

    setResettingVotes(true);
    try {
      await axios.post(`${API}/admin/votes/reset`);
      setStats({ total_proposals: 0, total_votes: 0, total_users: 0 });
      setNominations((prev) => prev.map((nom) => ({ ...nom, proposal_count: 0 })));
      await fetchData();
      window.alert('Les votes ont bien ete remis a zero.');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Impossible de reinitialiser les votes.';
      window.alert(detail);
    } finally {
      setResettingVotes(false);
    }
  };

  return (
    <main className="min-h-screen bg-obsidian" data-testid="home-page">
      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <ConstellationSky supporters={supporters} />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-electric-blue/10 blur-[150px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 pt-24 pb-10">
          <div className="text-center">
              <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 border border-gold/30 bg-gold/5">
                <Star className="w-4 h-4 text-gold" />
                <span className="text-sm uppercase tracking-widest text-gold">Édition 2026</span>
              </div>
              
              <h1 className="font-syne text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter uppercase text-white mb-4">
                STERED<br />
                <span className="text-gold-gradient">AN NOZ</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white/60 max-w-xl mx-auto mb-10">
                La célébration de la musique bretonne
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
                <Link
                  to="/proposer"
                  className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-8 py-4 text-sm transition-all hover:scale-105 inline-flex items-center justify-center gap-2"
                  data-testid="cta-proposer"
                >
                  Proposer un artiste
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/voter"
                  className="border border-white/20 hover:border-gold text-white px-8 py-4 uppercase tracking-wider text-sm hover:bg-gold/5 transition-all inline-flex items-center justify-center gap-2"
                  data-testid="cta-voter"
                >
                  Voter
                </Link>
                <Link
                  to="/carte-etoiles"
                  className="border border-electric-cyan/45 hover:border-electric-cyan text-electric-cyan px-8 py-4 uppercase tracking-wider text-sm hover:bg-electric-cyan/10 transition-all inline-flex items-center justify-center gap-2"
                >
                  Explorer la carte des etoiles
                </Link>
              </div>

              <div className="mb-8">
                <Link
                  to="/carte-etoiles"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-obsidian/45 px-5 py-2 text-xs uppercase tracking-widest text-white/80 hover:border-gold/55 hover:text-gold transition-colors"
                >
                  Cliquer ici pour deplier la carte en plein ecran
                </Link>
              </div>

              {/* Counters */}
              <div className="flex justify-center gap-12 md:gap-20">
                <div className="text-center">
                  <div className="font-syne text-4xl md:text-5xl font-bold text-gold">
                    <AnimatedCounter end={stats.total_proposals ?? 0} />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-white/40 mt-1">Propositions</div>
                </div>
                <div className="text-center">
                  <div className="font-syne text-4xl md:text-5xl font-bold text-white">
                    <AnimatedCounter end={stats.total_votes ?? 0} />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-white/40 mt-1">Votes</div>
                </div>
                <div className="text-center">
                  <div className="font-syne text-4xl md:text-5xl font-bold text-electric-cyan">
                    <AnimatedCounter end={stats.total_users ?? 0} />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-white/40 mt-1">Participants</div>
                </div>
              </div>

              {user?.is_admin && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={handleResetVotes}
                    disabled={resettingVotes}
                    className="inline-flex items-center gap-2 rounded-lg border border-red-500/45 bg-red-500/10 px-4 py-2 text-xs uppercase tracking-wider text-red-300 hover:bg-red-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {resettingVotes ? 'Reinitialisation...' : 'Remettre le compteur de votes a zero'}
                  </button>
                </div>
              )}
          </div>
        </div>
      </section>

      {/* 3 ÉTAPES */}
      <section className="py-20 bg-obsidian-light">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">
              Comment ça marche ?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <div key={step.num} className="relative text-center group">
                {idx < 2 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-gold/50 to-transparent" />
                )}
                <div className="w-24 h-24 mx-auto mb-6 border border-gold/30 bg-gold/5 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <span className="font-syne text-4xl font-bold text-gold">{step.num}</span>
                </div>
                <h3 className="font-syne text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-white/50">{step.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/proposer"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-gold text-white px-6 py-3 text-sm uppercase tracking-wider hover:bg-gold/5 transition-all"
            >
              Participer maintenant <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* APERÇU CATÉGORIES */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:pr-2">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">
                6 Catégories
              </h2>
              <p className="text-white/50 mt-2">Découvrez les récompenses</p>
            </div>
            <Link to="/categories" className="hidden sm:inline-flex items-center gap-2 text-gold hover:text-gold-glow transition-colors">
              Voir toutes <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/categories#${cat.id}`}
                  className="p-6 border border-white/10 bg-obsidian-light hover:border-gold/50 transition-all group"
                >
                  <Music className="w-6 h-6 text-gold mb-3" />
                  <h3 className="font-syne font-bold text-white group-hover:text-gold transition-colors">
                    {cat.name}
                  </h3>
                </Link>
              ))}
            </div>

            <aside className="lg:col-span-5 lg:justify-self-end lg:w-[460px]">
              <div className="mb-4 flex items-center gap-2 text-gold">
                <Music className="w-4 h-4" />
                <p className="text-xs uppercase tracking-[0.22em] text-gold/90">Playlist officielle</p>
              </div>

              <div className="rounded-2xl border border-gold/25 bg-obsidian-light/80 p-2 sm:p-3 shadow-[0_0_55px_rgba(0,183,255,0.08)]">
                <iframe
                  data-testid="embed-iframe"
                  title="Playlist Spotify Stered An Noz"
                  style={{ borderRadius: '12px' }}
                  src="https://open.spotify.com/embed/playlist/1eh5rIPQ7aBNF4dh89rhC4?utm_source=generator&si=a241deb82f324af4"
                  width="100%"
                  height="352"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              </div>
            </aside>
          </div>
          
          <div className="text-center mt-8 sm:hidden">
            <Link to="/categories" className="text-gold">Voir les 6 catégories →</Link>
          </div>
        </div>
      </section>

      {/* APERÇU ARTISTES */}
      {nominations.length > 0 && (
        <section className="py-20 bg-obsidian-light">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">
                  Dernières Propositions
                </h2>
              </div>
              <Link to="/proposer" className="hidden sm:inline-flex items-center gap-2 text-gold hover:text-gold-glow transition-colors">
                Voir toutes <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {nominations.map((nom) => (
                <div
                  key={nom.id}
                  className="p-6 border border-white/10 hover:border-gold/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold/20 to-electric-blue/20 flex items-center justify-center mb-4">
                    <Users className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-syne font-bold text-white truncate">{nom.nominee_name}</h3>
                  <p className="text-sm text-white/40 mt-1">{nom.proposal_count} propositions</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MINI CTA FOOTER */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-syne text-2xl md:text-3xl font-bold text-white mb-6">
            Prêt à participer ?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/proposer"
              className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-8 py-4 text-sm transition-all"
            >
              Proposer un artiste
            </Link>
            <Link
              to="/categories"
              className="border border-white/20 hover:border-white text-white px-8 py-4 uppercase tracking-wider text-sm hover:bg-white/5 transition-all"
            >
              Découvrir les catégories
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
